import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Building2, Search, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import AnimatedButton from '../components/AnimatedButton';

const Home: React.FC = () => {
  const { getThemeStyles } = useTheme();
  const themeStyles = getThemeStyles();

  const featureCards = [
    {
      icon: Search,
      title: 'Screening rapido',
      text: 'Trova informazioni chiave in pochi minuti, con un flusso pensato per proprietari e agenzie.'
    },
    {
      icon: ShieldCheck,
      title: 'Compliance GDPR',
      text: 'Consensi, audit e tracciabilità integrati per gestire dati sensibili in modo sicuro.'
    },
    {
      icon: BadgeCheck,
      title: 'Decisioni affidabili',
      text: 'Unisci valutazioni, storico e segnali di rischio in una vista unica e leggibile.'
    }
  ];

  const socialProof = [
    {
      score: '4.9/5',
      quote: 'Da quando usiamo Grented riduciamo drasticamente candidati problematici e tempi morti.',
      author: 'Studio Immobiliare Cittadino'
    },
    {
      score: '98%',
      quote: 'Onboarding chiaro, dashboard pulita, tutto molto piu rapido rispetto ai fogli condivisi.',
      author: 'Property Manager Team'
    },
    {
      score: '24h',
      quote: 'In media prendiamo una decisione in un giorno, prima ci volevano settimane.',
      author: 'Network Proprietari Milano'
    }
  ];

  return (
    <div className="home21-root" style={{ color: themeStyles.text }}>
      <div className="home21-aurora home21-aurora--left" aria-hidden="true" />
      <div className="home21-aurora home21-aurora--right" aria-hidden="true" />

      <section className="home21-hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="home21-hero-copy"
        >
          <span className="home21-kicker">
            <Sparkles size={16} />
            Tenant intelligence platform
          </span>

          <h1 className="home21-title">
            Valuta candidati in modo
            <span> rapido, sicuro e premium.</span>
          </h1>

          <p className="home21-subtitle">
            Grented unisce reputazione, segnali di rischio e consenso GDPR in un unico flusso elegante.
            Meno intuizione, piu decisioni solide.
          </p>

          <div className="home21-cta-row">
            <Link to="/register" className="home21-link-reset">
              <AnimatedButton size="lg" className="home21-cta-primary">
                Inizia ora
                <ArrowRight size={18} />
              </AnimatedButton>
            </Link>
            <Link to="/login" className="home21-link-reset">
              <AnimatedButton variant="secondary" size="lg" className="home21-cta-secondary">
                Accedi alla piattaforma
              </AnimatedButton>
            </Link>
          </div>

          <div className="home21-metrics">
            <div>
              <strong>+10k</strong>
              <span>utenti verificati</span>
            </div>
            <div>
              <strong>4.9/5</strong>
              <span>soddisfazione media</span>
            </div>
            <div>
              <strong>24h</strong>
              <span>decisione media</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
          className="home21-hero-panel"
        >
          <div className="home21-panel-head">
            <Building2 size={20} />
            <span>Workflow proprietario</span>
          </div>
          <ul className="home21-panel-list">
            <li><Search size={16} /> Ricerca profilo e storico</li>
            <li><ShieldCheck size={16} /> Verifica compliance e documenti</li>
            <li><Users size={16} /> Confronto feedback multi-tenant</li>
            <li><BadgeCheck size={16} /> Decisione finale tracciata</li>
          </ul>
          <div className="home21-panel-bar">
            <span>Affidabilita profilo</span>
            <strong>92%</strong>
          </div>
          <div className="home21-progress">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: '92%' }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
            />
          </div>
        </motion.div>
      </section>

      <section className="home21-grid-section">
        {featureCards.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.article
              key={feature.title}
              className="home21-feature-card"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              whileHover={{ y: -6 }}
            >
              <div className="home21-feature-icon">
                <Icon size={18} />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </motion.article>
          );
        })}
      </section>

      <section className="home21-proof-section">
        <header>
          <p>Trusted by property teams</p>
          <h2>Un look premium supportato da risultati reali</h2>
        </header>

        <div className="home21-proof-grid">
          {socialProof.map((item, index) => (
            <motion.blockquote
              key={item.author}
              className="home21-proof-card"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.35, delay: index * 0.06 }}
            >
              <strong>{item.score}</strong>
              <p>{item.quote}</p>
              <cite>{item.author}</cite>
            </motion.blockquote>
          ))}
        </div>
      </section>

      <footer className="home21-footer">
        <span>© 2026 Grented. Tutti i diritti riservati.</span>
        <div>
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/terms-of-service">Termini</Link>
          <Link to="/faq">FAQ</Link>
        </div>
      </footer>
    </div>
  );
};

export default Home;